/**
 * DeviceRemoteAccessPanel — "AnyDesk ปัจจุบัน" ของเครื่อง (หน้าจัดการอุปกรณ์ › ดูรายละเอียด)
 *
 * ค่ามาจากรายการเช่าของสัญญาที่เครื่องอยู่ตอนนี้ (GET /devices/{id}/remote-access) — ไม่ใช่ค่าบน
 * ตาราง devices · แก้ผ่าน PUT /devices/{id}/remote-access ซึ่ง backend เขียนลงรายการเช่าเดียวกับที่
 * หน้ารายละเอียดการเช่า/เคสซ่อมแก้ (ค่าเดียว · 23 ก.ย. 2026) · เครื่องไม่อยู่ในสัญญา = แสดงขีด แก้ไม่ได้
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RiRemoteControlLine } from 'react-icons/ri';
import AnyDeskInlineEditor from './AnyDeskInlineEditor';
import remoteAccessService, { type DeviceRemoteAccess } from '../../services/remoteAccessService';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

interface Props {
  deviceId: number;
  className?: string;
}

const DeviceRemoteAccessPanel: React.FC<Props> = ({ deviceId, className }) => {
  const [data, setData] = useState<DeviceRemoteAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await remoteAccessService.getDevice(deviceId));
    } catch (e) {
      setError(getApiErrorMessage(e, 'โหลดข้อมูล AnyDesk ไม่สำเร็จ'));
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const inRental = !!data?.rental_item_id;

  return (
    <div className={className ?? 'mt-4 p-4 bg-secondary-100 rounded-lg'} data-testid="device-remote-access">
      <h4 className="text-sm font-medium text-secondary-700 mb-2 flex items-center gap-1.5">
        <RiRemoteControlLine className="w-4 h-4" />
        AnyDesk ปัจจุบัน
      </h4>
      {loading ? (
        <p className="text-sm text-secondary-500">กำลังโหลด…</p>
      ) : error ? (
        <p className="text-sm text-danger-600">{error}</p>
      ) : inRental ? (
        <AnyDeskInlineEditor
          anydeskId={data?.anydesk_id}
          notes={data?.remote_access_notes}
          showLabel={false}
          sourceLabel={data?.rental_number ? `สัญญา ${data.rental_number}` : undefined}
          onSave={async (payload) => {
            const res = await remoteAccessService.updateDevice(deviceId, payload);
            setData(res);
          }}
          testId="device-anydesk"
        />
      ) : (
        <p className="text-sm text-secondary-500">
          — <span className="italic">(เครื่องไม่ได้อยู่ในสัญญา)</span>
        </p>
      )}
    </div>
  );
};

export default DeviceRemoteAccessPanel;
